import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, Mail, Building2, Inbox } from "lucide-react";
import { api } from "../../lib/api";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../ui/select";

const STATUSES = ["New", "Contacted", "Qualified", "Won", "Lost"];
const STATUS_COLORS = {
  New: "bg-brand/15 text-brand border-brand/30",
  Contacted: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  Qualified: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  Won: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  Lost: "bg-white/5 text-muted-foreground border-white/10",
};

export default function LeadsPanel() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/leads");
      setLeads(data);
    } catch {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (lead, status) => {
    setLeads((prev) => prev.map((l) => l.id === lead.id ? { ...l, status } : l));
    try {
      await api.patch(`/admin/leads/${lead.id}`, { status });
    } catch { toast.error("Update failed"); load(); }
  };

  const remove = async (lead) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await api.delete(`/admin/leads/${lead.id}`);
      setLeads((prev) => prev.filter((l) => l.id !== lead.id));
      toast.success("Lead deleted");
    } catch { toast.error("Delete failed"); }
  };

  const counts = STATUSES.reduce((acc, s) => { acc[s] = leads.filter((l) => (l.status || "New") === s).length; return acc; }, {});
  const shown = filter === "All" ? leads : leads.filter((l) => (l.status || "New") === filter);

  return (
    <div data-testid="panel-leads">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading text-2xl font-medium text-white">Leads</h2>
          <p className="text-sm text-muted-foreground mt-1">{leads.length} total discovery-call request{leads.length === 1 ? "" : "s"}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {["All", ...STATUSES].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`filter-${s.toLowerCase()}`}
            className={`rounded-full px-3.5 py-1.5 text-xs font-medium border transition-colors ${filter === s ? "bg-white text-gray-900 border-white" : "bg-white/5 text-muted-foreground border-white/10 hover:text-white"}`}
          >
            {s} {s !== "All" && <span className="opacity-70">({counts[s] || 0})</span>}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-3 text-muted-foreground py-16 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>
      ) : shown.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground border border-dashed border-white/10 rounded-2xl">
          <Inbox className="h-8 w-8 mx-auto mb-3 opacity-50" />
          No leads here yet.
        </div>
      ) : (
        <div className="space-y-3">
          {shown.map((lead) => (
            <div key={lead.id} className="rounded-2xl border border-white/5 bg-white/[0.015] p-5" data-testid="lead-row">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-white font-medium">{lead.name}</span>
                    <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${STATUS_COLORS[lead.status || "New"]}`}>{lead.status || "New"}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-muted-foreground">
                    <a href={`mailto:${lead.email}`} className="inline-flex items-center gap-1.5 hover:text-brand"><Mail className="h-3.5 w-3.5" />{lead.email}</a>
                    {lead.company && <span className="inline-flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" />{lead.company}</span>}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {lead.projectType && <span className="rounded-md bg-white/5 border border-white/10 px-2 py-0.5 text-xs text-white/70">{lead.projectType}</span>}
                    {lead.budget && <span className="rounded-md bg-white/5 border border-white/10 px-2 py-0.5 text-xs text-white/70">{lead.budget}</span>}
                  </div>
                  <p className="mt-3 text-sm text-white/70 leading-relaxed">{lead.message}</p>
                  <div className="mt-2 text-xs text-muted-foreground/70">{new Date(lead.created_at).toLocaleString()}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Select value={lead.status || "New"} onValueChange={(v) => setStatus(lead, v)}>
                    <SelectTrigger data-testid="lead-status-select" className="w-36 bg-black/30 border-white/10 text-white rounded-lg h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-surface-2 border-white/10 text-white">
                      {STATUSES.map((s) => <SelectItem key={s} value={s} className="focus:bg-white/10 focus:text-white">{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <button onClick={() => remove(lead)} className="p-2 text-muted-foreground hover:text-red-400" data-testid="delete-lead"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
